import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { fetchStudentApplications } from 'api/applications'
import { fetchAppAssignments } from 'api/appAssignments'
import { fetchStudentLorBragsheet, fetchStudentLorRequests } from 'api/lorRequests'
import { listCounselorLorLetters } from 'api/counselorLor'
import { appAssignmentsStore } from 'pages/ApplicationsPage/model/appAssignmentsStore'
import { studentApplicationsStore } from '../../Applications/model/studentApplicationsStore'

const normalizeTargetId = app =>
	String(
		app?.applicationTargetId ||
			app?.application_target_id ||
			app?.id ||
			'',
	).trim()

const normalizeApp = app => ({
	applicationTargetId: normalizeTargetId(app),
	studentId: String(app?.studentId || app?.student_id || '').trim(),
	university: String(
		app?.university || app?.universityName || app?.university_name || '',
	).trim(),
	program: String(
		app?.program || app?.major || app?.programName || app?.courseName || '',
	).trim(),
	intake: String(
		app?.intake || app?.intakeSession || app?.intake_session || '',
	).trim(),
})

const buildBragsheetRows = (bragsheetData, assignment) => {
	const files = Array.isArray(bragsheetData?.bragsheets)
		? bragsheetData.bragsheets
		: []
	const rows = files
		.map((file, index) => {
			const fileId = String(file?.fileId || file?.file_id || '').trim()
			if (!fileId) return null
			return {
				id: `bragsheet-${fileId}`,
				title:
					files.length > 1
						? `Brag Sheet #${files.length - index}`
						: 'Brag Sheet',
				counselorStatus: 'success',
				fileId,
				lastUpdated:
					file.createdAt ||
					file.created_at ||
					file.updatedAt ||
					file.updated_at,
				kind: 'bragsheet',
			}
		})
		.filter(Boolean)

	if (rows.length) {
		return rows
	}

	// Fallback: ready meta with fileId (common when student_lor_files list is empty
	// but saas.student_bragsheets still points at the generated PDF).
	const meta = bragsheetData?.bragsheet || bragsheetData?.meta || {}
	const metaStatus = String(meta?.status || '').toLowerCase()
	const metaFileId = String(meta?.fileId || meta?.file_id || '').trim()
	if (metaStatus === 'ready') {
		return [
			{
				id: metaFileId ? `bragsheet-${metaFileId}` : 'bragsheet-ready',
				title: 'Brag Sheet',
				counselorStatus: 'success',
				fileId: metaFileId,
				lastUpdated:
					meta.updatedAt ||
					meta.updated_at ||
					meta.generatedAt ||
					meta.generated_at,
				kind: 'bragsheet',
			},
		]
	}

	const assignStatus = String(assignment?.status || 'missing').toLowerCase()
	if (
		['assigned', 'pending', 'not_started', 'in_progress'].includes(assignStatus)
	) {
		return [
			{
				id: 'bragsheet-assigned',
				title: 'Brag Sheet',
				counselorStatus: 'assigned',
				assignmentId: assignment?.assignmentId,
				taskId: assignment?.taskId,
				kind: 'bragsheet',
			},
		]
	}

	return [
		{
			id: 'bragsheet-missing',
			title: 'Brag Sheet',
			counselorStatus: 'missing',
			kind: 'bragsheet',
		},
	]
}

const buildApplicationCards = ({
	applications,
	counselorLetters,
	lorRequests,
	assignments,
}) => {
	const lettersByApp = new Map()
	for (const letter of counselorLetters || []) {
		const key = String(letter?.applicationTargetId || '').trim()
		if (!key) continue
		if (!lettersByApp.has(key)) lettersByApp.set(key, [])
		const status = String(letter?.status || '').toLowerCase()
		if (status !== 'draft' && status !== 'final') continue
		lettersByApp.get(key).push({
			id: letter.id,
			letterId: letter.id,
			title: letter.title || 'Letter of Recommendation',
			author:
				letter.authorName ||
				letter.authorDisplayName ||
				letter.authorCounselorName ||
				letter.author?.name ||
				'Counselor',
			updatedAt: letter.updatedAt || letter.generatedAt || letter.createdAt,
		})
	}

	const requestsByApp = new Map()
	for (const req of lorRequests || []) {
		const key = String(req?.applicationTargetId || req?.application_target_id || '').trim()
		if (!key) continue
		if (!requestsByApp.has(key)) requestsByApp.set(key, [])
		requestsByApp.get(key).push(req)
	}

	const lorAssignmentsByApp = new Map()
	for (const item of assignments || []) {
		const type = String(item?.type || '').toLowerCase()
		if (type !== 'lor') continue
		const key = String(item?.applicationTargetId || '').trim()
		if (!key) continue
		if (!lorAssignmentsByApp.has(key)) lorAssignmentsByApp.set(key, [])
		lorAssignmentsByApp.get(key).push(item)
	}

	return (applications || []).map(app => {
		const applicationTargetId = normalizeTargetId(app)
		const letters = (lettersByApp.get(applicationTargetId) || []).sort((a, b) => {
			const at = new Date(a.updatedAt || 0).getTime()
			const bt = new Date(b.updatedAt || 0).getTime()
			return bt - at
		})

		const requests = requestsByApp.get(applicationTargetId) || []
		const uploaded = requests
			.filter(req => req?.receivedLorFileId || req?.received_lor_file_id)
			.map((req, index) => ({
				id: String(req.id || `upload-${index}`),
				title: 'LoR',
				counselorStatus: 'success',
				fileId: req.receivedLorFileId || req.received_lor_file_id,
				lastUpdated:
					req.updatedAt ||
					req.updated_at ||
					req.createdAt ||
					req.created_at,
				requestId: req.id,
			}))

		const lorAssignments = lorAssignmentsByApp.get(applicationTargetId) || []
		const pendingAssignment = lorAssignments.find(item => {
			const status = String(item?.status || '').toLowerCase()
			return status !== 'completed' && status !== 'done'
		})
		const pendingRequest = requests.find(req => {
			if (req?.receivedLorFileId || req?.received_lor_file_id) return false
			const status = String(req?.status || '').toLowerCase()
			return !['completed', 'done', 'cancelled', 'declined'].includes(status)
		})

		const studentRows = [...uploaded]
		if (!uploaded.length && (pendingAssignment || pendingRequest)) {
			const pendingItem = pendingAssignment || pendingRequest
			studentRows.push({
				id: `lor-assigned-${pendingItem.id}`,
				title: pendingItem.title || 'Letter of Recommendation',
				counselorStatus: 'assigned',
				assignmentId: pendingAssignment?.id,
				requestId: pendingRequest?.id,
				lastUpdated:
					pendingItem.dueDate ||
					pendingItem.startDate ||
					pendingItem.updatedAt ||
					pendingItem.createdAt,
			})
		}

		const hasCounselorLetters = letters.length > 0
		const hasStudentContent = studentRows.length > 0
		const isEmpty = !hasCounselorLetters && !hasStudentContent

		return {
			...normalizeApp(app),
			counselorLetters: letters.map((letter, index) => ({
				...letter,
				label: `LoR #${letters.length - index}`,
			})),
			studentRows,
			showAssignStudentFooter: !hasStudentContent,
			isEmpty,
		}
	})
}

export const RECOMMENDATION_FILTER_STATUSES = [
	{ id: 'new', label: 'New' },
	{ id: 'written', label: 'Written' },
	{ id: 'received', label: 'Received' },
	{ id: 'awaiting', label: 'Awaiting' },
	{ id: 'none', label: 'No activity' },
]

const readSeenLorIds = studentId => {
	try {
		const stored = window.localStorage.getItem(`seen-lors:${studentId}`)
		return new Set(stored ? JSON.parse(stored) : [])
	} catch {
		return new Set()
	}
}

const getCardCategories = (card, seenLorIds) => {
	const categories = []
	const studentRows = card?.studentRows || []
	const receivedRows = studentRows.filter(
		row => row.counselorStatus === 'success',
	)
	if (receivedRows.some(row => !seenLorIds.has(String(row.id)))) {
		categories.push('new')
	}
	if ((card?.counselorLetters || []).length > 0) categories.push('written')
	if (receivedRows.length > 0) categories.push('received')
	if (studentRows.some(row => row.counselorStatus === 'assigned')) {
		categories.push('awaiting')
	}
	if (card?.isEmpty) categories.push('none')
	return categories
}

export const useStudentRecommendationsTab = (studentId, isActive = false) => {
	const [loadStatus, setLoadStatus] = useState('idle')
	const [error, setError] = useState('')
	const [bragsheetData, setBragsheetData] = useState(null)
	const [counselorLetters, setCounselorLetters] = useState([])
	const [lorRequests, setLorRequests] = useState([])
	const [hasLoadedOnce, setHasLoadedOnce] = useState(false)
	const [selectedCategories, setSelectedCategories] = useState([])
	const inFlightRef = useRef(false)
	const hasLoadedOnceRef = useRef(false)

	const reload = useCallback(async ({ silent = false } = {}) => {
		const sid = String(studentId || '').trim()
		if (!sid || inFlightRef.current) return
		inFlightRef.current = true
		const keepUi = silent || hasLoadedOnceRef.current
		if (!keepUi) {
			setLoadStatus('loading')
		}
		setError('')

		try {
			const appsPromise = studentApplicationsStore.hasDataFor(sid)
				? Promise.resolve({ applications: studentApplicationsStore.applications })
				: fetchStudentApplications(sid).then(data => {
						studentApplicationsStore.setApplications(sid, data?.applications || [])
						return data
					})

			const assignmentsPromise =
				appAssignmentsStore.assignments.length > 0
					? Promise.resolve({
							assignments: appAssignmentsStore.assignments,
						})
					: fetchAppAssignments().then(data => {
							appAssignmentsStore.setPayload(data)
							return data
						})

			const [appsRes, bragRes, lettersRes, requestsRes, assignmentsRes] =
				await Promise.all([
					appsPromise,
					fetchStudentLorBragsheet(sid),
					listCounselorLorLetters(sid).catch(() => ({ letters: [] })),
					fetchStudentLorRequests(sid).catch(() => ({ requests: [] })),
					assignmentsPromise,
				])

			if (!studentApplicationsStore.hasDataFor(sid)) {
				studentApplicationsStore.setApplications(sid, appsRes?.applications || [])
			}

			setBragsheetData(bragRes)
			setCounselorLetters(lettersRes?.letters || [])
			setLorRequests(requestsRes?.requests || [])
			hasLoadedOnceRef.current = true
			setHasLoadedOnce(true)
			setLoadStatus('success')
			void assignmentsRes
		} catch (e) {
			setError(e?.message || 'Failed to load recommendations')
			setLoadStatus('error')
		} finally {
			inFlightRef.current = false
		}
	}, [studentId])

	useEffect(() => {
		if (!isActive || !studentId) return undefined
		studentApplicationsStore.ensureStudent(studentId)
		reload()
		return undefined
	}, [isActive, studentId, reload])

	const bragsheetRows = useMemo(
		() => buildBragsheetRows(bragsheetData, bragsheetData?.assignment),
		[bragsheetData],
	)

	const allApplicationCards = buildApplicationCards({
		applications: studentApplicationsStore.applications,
		counselorLetters,
		lorRequests,
		assignments: appAssignmentsStore.assignments,
	})

	const seenLorIds = readSeenLorIds(studentId)

	const countsByStatus = RECOMMENDATION_FILTER_STATUSES.reduce((acc, status) => {
		acc[status.id] = 0
		return acc
	}, {})
	allApplicationCards.forEach(card => {
		getCardCategories(card, seenLorIds).forEach(category => {
			if (category in countsByStatus) countsByStatus[category] += 1
		})
	})

	const applicationCards =
		selectedCategories.length === 0
			? allApplicationCards
			: allApplicationCards.filter(card =>
					getCardCategories(card, seenLorIds).some(category =>
						selectedCategories.includes(category),
					),
				)

	const toggleStatus = useCallback(statusId => {
		const key = String(statusId || '').trim()
		if (!key) return
		setSelectedCategories(prev =>
			prev.includes(key) ? prev.filter(v => v !== key) : [...prev, key],
		)
	}, [])

	const clearStatusFilters = useCallback(() => setSelectedCategories([]), [])

	const isStatusActive = useCallback(
		statusId => selectedCategories.includes(statusId),
		[selectedCategories],
	)

	const filters = {
		statuses: RECOMMENDATION_FILTER_STATUSES,
		countsByStatus,
		totalCount: allApplicationCards.length,
		isAllActive: selectedCategories.length === 0,
		toggleStatus,
		clearStatusFilters,
		isStatusActive,
	}

	return {
		loadStatus,
		error,
		bragsheetRows,
		applicationCards,
		filters,
		hasLoadedOnce,
		reload,
	}
}

export default useStudentRecommendationsTab
